import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { SUPPORTED_LOCALES, setLocale, type Locale } from '@/lib/i18n'

import { useAdminMutation } from '../api'
import { AdminHeader } from '../AdminLayout'
import { useAdminContext, useAdminSession } from '../session'

/** La règle du serveur, répétée ici pour prévenir avant d'envoyer. */
const MIN_LENGTH = 12

/**
 * « Mon compte » : qui je suis, mon mot de passe, ma langue.
 *
 * L'écran ne dépend d'aucune édition. `me` (lu avec l'édition choisie) peut
 * manquer — compte sans édition, liste pas encore chargée — et la session
 * locale porte déjà le nom, l'adresse et la langue : on retombe dessus. Le
 * changement de mot de passe, lui, ne demande aucun en-tête d'édition.
 */
export function AdminAccountPage() {
  const { t } = useTranslation()
  const { me, eventId } = useAdminContext()
  const { session } = useAdminSession()
  const user = me?.user ?? session.user
  // Une fois le mot de passe changé, l'avertissement disparaît tout de suite,
  // sans attendre le rechargement de `me` (ni dépendre de la session locale,
  // qui garde l'ancien drapeau jusqu'à la prochaine connexion).
  const [replaced, setReplaced] = useState(false)

  useEffect(() => {
    document.title = t('admin.account.title')
  }, [t])

  const mustChange = Boolean(user.must_change_password) && !replaced

  return (
    <>
      <AdminHeader title={t('admin.account.title')} description={t('admin.account.description')} />

      {mustChange ? (
        <div
          role="alert"
          className="mb-6 rounded-lg border border-state-warning/50 bg-state-warning/10 px-4 py-4"
        >
          <p className="text-sm font-medium text-state-warning">
            {t('admin.account.mustChangeTitle')}
          </p>
          <p className="mt-1 text-sm text-content-secondary">{t('admin.account.mustChangeBody')}</p>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-line-subtle bg-bg-elevated p-4 sm:p-6">
          <h2 className="font-display text-heading-md text-content-primary">
            {t('admin.account.identity')}
          </h2>
          <dl className="mt-4 space-y-3 text-sm">
            <div>
              <dt className="text-caption uppercase tracking-wide text-content-muted">
                {t('admin.account.name')}
              </dt>
              <dd className="mt-0.5 break-words text-content-primary">{user.full_name}</dd>
            </div>
            <div>
              <dt className="text-caption uppercase tracking-wide text-content-muted">
                {t('admin.email')}
              </dt>
              <dd className="mt-0.5 break-all text-content-primary">{user.email}</dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-content-muted">{t('admin.account.identityHint')}</p>

          <LanguageField eventId={eventId} />
        </section>

        <PasswordForm
          email={user.email}
          eventId={eventId}
          onChanged={() => setReplaced(true)}
        />
      </div>
    </>
  )
}

/**
 * La langue de l'interface, enregistrée sur le compte.
 *
 * Même geste que le sélecteur de la barre latérale : on bascule l'écran tout
 * de suite, puis on enregistre — la langue suit alors la personne sur un
 * autre appareil, et les e-mails du système partent dans cette langue.
 */
function LanguageField({ eventId }: { eventId: string | undefined }) {
  const { t, i18n } = useTranslation()
  const save = useAdminMutation<unknown, { locale: Locale }>(
    'put',
    () => '/auth/me/locale',
    eventId,
    [['admin-me']],
  )

  return (
    <label className="mt-6 block">
      <span className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.language')}
      </span>
      <select
        value={i18n.language}
        onChange={(changeEvent) => {
          const locale = changeEvent.target.value as Locale
          void setLocale(locale)
          save.mutate({ locale })
        }}
        className="mt-1.5 w-full rounded-md border border-line bg-bg-base px-3 py-2.5 text-content-primary"
      >
        {SUPPORTED_LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {t(`admin.mail.localeName.${locale}`)}
          </option>
        ))}
      </select>
      <span className="mt-1.5 block text-xs text-content-muted">
        {save.isError ? (
          <span className="text-state-danger">{t('admin.account.localeFailed')}</span>
        ) : save.isSuccess ? (
          t('admin.saved')
        ) : (
          t('admin.account.localeHint')
        )}
      </span>
    </label>
  )
}

/**
 * Changer son mot de passe.
 *
 * L'ancien est exigé : une session laissée ouverte sur une tablette de
 * l'entrée ne doit pas suffire à s'approprier le compte. Le serveur compte un
 * ancien mot de passe faux comme une connexion ratée — d'où un message
 * explicite plutôt qu'un « erreur » qu'on retenterait cinq fois.
 */
function PasswordForm({
  email,
  eventId,
  onChanged,
}: {
  email: string
  eventId: string | undefined
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)

  const change = useAdminMutation<
    { message: string },
    { current_password: string; new_password: string }
  >('put', () => '/auth/me/password', eventId, [['admin-me']])

  // Les vérifications locales ne font que prévenir : le serveur refait les
  // siennes (longueur, différent de l'actuel, différent de l'adresse).
  const tooShort = next.length > 0 && next.length < MIN_LENGTH
  const mismatch = confirm.length > 0 && confirm !== next
  const sameAsCurrent = next.length > 0 && next === current
  const ready =
    current.length > 0 && next.length >= MIN_LENGTH && next === confirm && !sameAsCurrent

  let errorMessage: string | null = null
  if (change.error instanceof ApiError) {
    errorMessage =
      change.error.code === 'CURRENT_PASSWORD_INVALID'
        ? t('admin.account.currentInvalid')
        : change.error.message
  } else if (change.error) {
    errorMessage = t('admin.account.changeFailed')
  }

  return (
    <section className="rounded-lg border border-line-subtle bg-bg-elevated p-4 sm:p-6">
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.account.passwordTitle')}
      </h2>
      <p className="mt-1 text-sm text-content-secondary">{t('admin.account.passwordHint')}</p>

      <form
        className="mt-5 space-y-4"
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault()
          if (!ready || change.isPending) return
          setDone(false)
          change.mutate(
            { current_password: current, new_password: next },
            {
              onSuccess: () => {
                // Les champs se vident : un mot de passe ne doit pas rester
                // dans le DOM d'un écran qu'on laisse ouvert.
                setCurrent('')
                setNext('')
                setConfirm('')
                setDone(true)
                onChanged()
              },
            },
          )
        }}
      >
        {/* L'identifiant, invisible, pour que le gestionnaire de mots de
            passe associe le nouveau mot de passe au bon compte. */}
        <input
          type="email"
          name="username"
          autoComplete="username"
          value={email}
          readOnly
          hidden
        />

        <PasswordInput
          label={t('admin.account.currentPassword')}
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
        />
        <PasswordInput
          label={t('admin.account.newPassword')}
          value={next}
          onChange={setNext}
          autoComplete="new-password"
          hint={
            tooShort
              ? t('admin.account.tooShort', { count: MIN_LENGTH })
              : sameAsCurrent
                ? t('admin.account.sameAsCurrent')
                : t('admin.account.newPasswordHint', { count: MIN_LENGTH })
          }
          invalid={tooShort || sameAsCurrent}
        />
        <PasswordInput
          label={t('admin.account.confirmPassword')}
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          {...(mismatch ? { hint: t('admin.account.mismatch') } : {})}
          invalid={mismatch}
        />

        {errorMessage ? <p className="text-sm text-state-danger">{errorMessage}</p> : null}
        {done ? (
          <p role="status" className="text-sm text-state-success">
            {t('admin.account.changed')}
          </p>
        ) : null}

        <Button type="submit" size="sm" disabled={!ready || change.isPending}>
          {change.isPending ? t('state.loading') : t('admin.account.submit')}
        </Button>
      </form>
    </section>
  )
}

function PasswordInput({
  label,
  value,
  onChange,
  autoComplete,
  hint,
  invalid = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  autoComplete: 'current-password' | 'new-password'
  hint?: string
  invalid?: boolean
}) {
  const { t } = useTranslation()
  // Afficher le mot de passe : sur un téléphone, une faute de frappe dans un
  // champ masqué de douze caractères se paie d'un compte bloqué.
  const [visible, setVisible] = useState(false)

  return (
    <label className="block">
      <span className="text-caption uppercase tracking-wide text-content-muted">{label}</span>
      <span className="mt-1.5 flex items-stretch gap-2">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(changeEvent) => onChange(changeEvent.target.value)}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className={cn(
            'min-w-0 flex-1 rounded-md border bg-bg-base px-3 py-2.5 text-content-primary',
            invalid ? 'border-state-danger/60' : 'border-line',
          )}
        />
        <button
          type="button"
          onClick={() => setVisible((shown) => !shown)}
          aria-pressed={visible}
          className="shrink-0 rounded-md border border-line px-3 text-xs text-content-secondary transition hover:text-content-primary"
        >
          {visible ? t('admin.account.hide') : t('admin.account.show')}
        </button>
      </span>
      {hint ? (
        <span
          className={cn(
            'mt-1.5 block text-xs',
            invalid ? 'text-state-danger' : 'text-content-muted',
          )}
        >
          {hint}
        </span>
      ) : null}
    </label>
  )
}
